import { Component, h, ComponentInterface, Host, State } from "@stencil/core";
import { AppConfigService as Config, ErrorHandler } from "../../services";
import { parse } from "@saasquatch/stencil-html-parser";
import { HandledError } from "../../models/HandledError";
import { ErrorCode } from "../../models/enums";

interface IMediaPlayerState {
  muted?: boolean;
  videoSrc?: string;
  embedSrc?: string;
  open?: boolean;
  loading?: boolean;
}

@Component({
  tag: "app-media-player",
  styleUrl: "app-media-player.scss",
  shadow: true,
})
export class AppMediaPlayer implements ComponentInterface {
  // Indicate that name should be a public property on the component
  @State() viewState: IMediaPlayerState = {
    muted: true,
    open: false,
    loading: true,
  };

  private videoRef: HTMLVideoElement;

  componentDidLoad() {
    const open = !!Config.queueVideoUrl || !!Config.queueEmbeddableVideoCode;
    this.setViewState({
      videoSrc: Config.queueVideoUrl,
      embedSrc: Config.queueEmbeddableVideoCode,
      open,
    });
  }

  toggleMute() {
    if (this.videoRef.muted) {
      this.videoRef.muted = false;
      this.setViewState({ muted: false });
    } else {
      this.videoRef.muted = true;
      this.setViewState({ muted: true });
    }
  }

  canPlayThrough() {
    // try to play
    this.videoRef.play()?.catch((_) => {
        // mute and try to play again
      this.videoRef.muted = true;
      this.videoRef.play().catch((_) => {
        // check every 3 seconds if user has interacted with page and try to play again
        let playAttempt = setInterval(() => {
          this.videoRef.play()?.then(() => {
            clearInterval(playAttempt);
          });
        }, 3000);
      });
    });
  }

  volumeChange() {
    this.setViewState({ muted: this.videoRef.muted });
  }

  loadedData() {
    this.setViewState({ muted: this.videoRef.muted, loading: false });
  }

  private setViewState(state: IMediaPlayerState) {
    this.viewState = { ...this.viewState, ...state };
  }

  render() {
    const cl = {
      "av-media-player": true,
    };
    const { videoSrc, embedSrc, open } = this.viewState;
    let html = null;
    if (open && embedSrc) {
      try {
        // seems that sanitize removes the iframe
        // const sanitized = DOMPurify.sanitize(embed);
        // if (sanitized) {
        // html = parse(sanitized)
        // }
        html = parse(embedSrc);
      } catch (ex) {
        ErrorHandler.log(
          new HandledError(ex, ErrorCode.MEDIA_PLAYER_HTML_PARSE_FAILED)
        );
      }
    }
    return (
      <Host>
        <av-card open={this.viewState.open} class={cl}>
          {embedSrc && html}
          {!!videoSrc && !embedSrc && (
            <video
              src={videoSrc}
              ref={(e) => (this.videoRef = e)}
              onLoadedData={this.loadedData.bind(this)}
              onCanPlayThrough={this.canPlayThrough.bind(this)}
              onVolumeChange={this.volumeChange.bind(this)}
              id="av-media-player-video"
              preload="auto"
              // muted
              // controls
              autoplay
              playsinline
              webkit-playsinline="true"
              loop
            ></video>
          )}

          {!this.viewState.loading && (
            <av-button
              shape="round"
              size="sm"
              class="av-media-player-control"
              onClick={this.toggleMute.bind(this)}
            >
              {!this.viewState.muted && <av-asset image="speaker"></av-asset>}
              {this.viewState.muted && (
                <av-asset image="speaker-muted"></av-asset>
              )}
            </av-button>
          )}
        </av-card>
      </Host>
    );
  }
}
